import { useAppDispatch, useAppSelector } from "@app/hooks"
import { CourseInstanceDto } from "@clients/courseInstanceClient"
import { TermDto } from "@clients/termClient"
import BackButton from "@cmp/buttons/backButton"
import { selectCourseInstanceByIdentifiers } from "@features/courseInstance/courseInstanceSelectors"
import { createCourseInstanceWithClasses } from "@features/courseInstance/courseInstanceSlice"
import { selectCourseDisplayName } from "@features/coursesMenu/coursesSelectors"
import { selectTerms } from "@features/term/termSelector"
import {
  CourseClassInformationLanguageEnum,
  CreateCourseInstanceWithClassesResponse,
} from "@masterschool/course-builder-api"
import { Box, Button, Stack, Typography } from "@mui/material"
import AppIcon from "@utils/appIcon"
import appIcons from "@utils/appIcons"
import { formatDateAsYearAndLongMonth } from "@utils/date"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import appTheme from "../theme/appTheme"
import GenericDialog from "@cmp/genericDialog"
import { selectIsUserFeatureFlagEnabled } from "@features/login/featureFlagSelectors"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import { Role } from "@features/login/loginSlice"

export function CourseInstanceHeader(props: {
  courseInstance: CourseInstanceDto
}) {
  const { courseInstance } = props

  const courseDisplayName = useAppSelector(
    selectCourseDisplayName(courseInstance.courseId),
  )

  const navigate = useNavigate()

  return (
    <Stack
      direction="row"
      sx={{
        py: "20px",
        width: "100%",
        borderBottom: `1px solid ${appTheme.palette.other.outlineBorder}`,
        bgcolor: appTheme.palette.primary.contrast,
        position: "relative",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box sx={{ position: "absolute", left: 24 }}>
        <BackButton
          text={"Courses"}
          onClick={() => navigate(`/course-manager`)}
        />
      </Box>
      <Stack direction="row" gap={1} alignItems="center">
        <Typography variant="h6">{courseDisplayName}</Typography>
      </Stack>
      <Stack sx={{ position: "absolute", right: 24 }}>
        <TermNavigator
          courseId={courseInstance.courseId}
          currentTermId={courseInstance.termId}
          courseName={courseDisplayName}
        />
      </Stack>
    </Stack>
  )
}

const TermNavigator = ({
  courseId,
  currentTermId,
  courseName,
}: {
  courseId: string
  currentTermId: string
  courseName: string | undefined
}) => {
  const [createInstanceModel, setCreateInstanceModel] = useState<{
    term: TermDto
  } | null>(null)
  const terms = useAppSelector(selectTerms)
  const navigate = useNavigate()
  const hasCreateCourseInstanceFlag = useCanCreateCourseInstance()

  const currentTermIndex = useMemo(() => {
    if (terms.length === 0) return -1

    return terms.findIndex((term) => term.id === currentTermId)
  }, [terms, currentTermId])

  const currentTerm = terms[currentTermIndex]
  const previousTerm = terms[currentTermIndex - 1]
  const previousTermId = previousTerm?.id || undefined
  const nextTerm = terms[currentTermIndex + 1]
  const nextTermId = nextTerm?.id || undefined

  const nextTermCourseInstance = useAppSelector((state) =>
    selectCourseInstanceByIdentifiers(state, courseId, nextTermId, undefined),
  )
  const previousTermCourseInstance = useAppSelector((state) =>
    selectCourseInstanceByIdentifiers(
      state,
      courseId,
      previousTermId,
      undefined,
    ),
  )

  const onCreateInstance = (term: TermDto) => {
    if (!courseName) {
      console.error("Course name is not defined")
      return
    }
    setCreateInstanceModel({ term })
  }

  if (terms.length === 0 || currentTermIndex === -1) return null

  return (
    <Stack direction="row" gap="32px" alignItems="center">
      {createInstanceModel && (
        <CreateCourseInstanceModal
          courseId={courseId}
          term={createInstanceModel.term}
          courseName={courseName}
          onClose={() => setCreateInstanceModel(null)}
        />
      )}
      {!previousTermCourseInstance &&
        previousTerm &&
        hasCreateCourseInstanceFlag && (
          <Button
            variant="outlined"
            size="tiny"
            onClick={() => onCreateInstance(previousTerm)}
          >
            Create {formatDateAsYearAndLongMonth(previousTerm.startDate)}{" "}
            instance
          </Button>
        )}
      {previousTermCourseInstance && (
        <AppIcon
          icon={appIcons.chevronRight}
          props={{
            sx: {
              transform: "scaleX(-1)",
              cursor: "pointer",
            },
            onClick: () => {
              navigate(
                `/course-manager/course-instance/${previousTermCourseInstance.id}`,
              )
            },
          }}
        />
      )}
      <Typography variant="body1_sb">
        {currentTerm?.startDate
          ? formatDateAsYearAndLongMonth(currentTerm.startDate)
          : ""}
      </Typography>
      {nextTermCourseInstance && (
        <AppIcon
          icon={appIcons.chevronRight}
          props={{
            onClick: () => {
              navigate(
                `/course-manager/course-instance/${nextTermCourseInstance.id}`,
              )
            },
            sx: { cursor: "pointer" },
          }}
        />
      )}
      {!nextTermCourseInstance && nextTermId && hasCreateCourseInstanceFlag && (
        <Button
          variant="outlined"
          size="tiny"
          onClick={() => onCreateInstance(nextTerm)}
        >
          Create {formatDateAsYearAndLongMonth(nextTerm.startDate)} instance
        </Button>
      )}
    </Stack>
  )
}

const CreateCourseInstanceModal = ({
  courseId,
  term,
  courseName,
  onClose,
}: {
  courseName?: string
  term: TermDto
  courseId: string
  onClose: () => void
}) => {
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const onCreateInstance = async () => {
    if (!courseName) {
      console.error("Course name is not defined")
      return
    }
    const courseClassName = `${courseName} ${formatDateAsYearAndLongMonth(
      term.startDate,
    )}`
    setLoading(true)
    dispatch(
      createCourseInstanceWithClasses({
        termId: term.id,
        courseId,
        majorVersion: undefined,
        courseClassesInformation: [
          {
            name: courseClassName,
            language: CourseClassInformationLanguageEnum.English,
          },
          {
            name: courseClassName,
            language: CourseClassInformationLanguageEnum.German,
          },
        ],
      }),
    )
      .then((res) => {
        const response = res.payload as CreateCourseInstanceWithClassesResponse
        if (response.isNewlyCreated) {
          navigate(
            `/course-manager/course-instance/${response.courseInstance.id}`,
          )
        }
      })
      .finally(() => {
        setLoading(false)
        onClose()
      })
  }

  return (
    <GenericDialog
      onClose={onClose}
      open
      size="sm"
      title="Create Course Instance"
      content={
        <Stack>
          <Typography variant="body1">
            Are you sure you want to create a new course instance?
          </Typography>
          <Typography variant="body2">
            (This action cannot be undone)
          </Typography>
          <Typography variant="body1_sb" pt={3}>
            {courseName} - {formatDateAsYearAndLongMonth(term.startDate)}
          </Typography>
          <Typography variant="body2" color="text.secondary" pt={1}>
            This will create two course classes, one in English and one in
            German.
          </Typography>
        </Stack>
      }
      buttons={[
        {
          type: "secondary",
          text: "Cancel",
          onClick: onClose,
        },
        {
          text: "Create",
          type: "primary",
          onClick: onCreateInstance,
          loading,
        },
      ]}
    />
  )
}

const useCanCreateCourseInstance = () => {
  const hasCreateCourseInstanceFlag = useAppSelector((state) =>
    selectIsUserFeatureFlagEnabled(state, "createCourseInstance"),
  )
  const user = useAppSelector(selectLoggedInUser)
  const roles = user?.roles || []
  const isAdmin = roles.includes(Role.LmsAdmin)

  return hasCreateCourseInstanceFlag && isAdmin
}
